import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import "@fontsource-variable/roboto";
import type { NavigateOptions, ToOptions } from "@tanstack/react-router";
import {
  createRootRouteWithContext,
  Outlet,
  useRouter,
} from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";
import React, { Suspense } from "react";
import { RouterProvider } from "react-aria";

import { useDocumentTitle } from "@/core/lib/document-title";
import type { RouteContext } from "@/core/lib/types";

declare module "@react-types/shared" {
  interface RouterConfig {
    href: ToOptions;
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}

declare module "react-aria-components" {
  interface RouterConfig {
    href: ToOptions;
    routerOptions: Omit<NavigateOptions, keyof ToOptions>;
  }
}

const TanStackDevtools =
  process.env.NODE_ENV === "production"
    ? () => null
    : React.lazy(() =>
        import("@tanstack/react-devtools").then((res) => ({
          default: res.TanStackDevtools,
        })),
      );

function Root() {
  const router = useRouter();
  useDocumentTitle();

  return (
    <RouterProvider
      navigate={(href, opts) => {
        const options =
          typeof href === "string" ? { href, ...opts } : { ...href, ...opts };

        return router.navigate(options);
      }}
      useHref={(href) => {
        if (href) {
          return router.buildLocation(href as ToOptions).href;
        }

        return "/";
      }}
    >
      <Suspense>
        <TanStackDevtools
          config={{}}
          plugins={[
            {
              name: "TanStack Query",
              render: <ReactQueryDevtoolsPanel />,
            },
            {
              name: "TanStack Router",
              render: <TanStackRouterDevtoolsPanel />,
            },
          ]}
        />
      </Suspense>

      <Outlet />
    </RouterProvider>
  );
}

export const Route = createRootRouteWithContext<RouteContext>()({
  component: Root,
});
